<template>
  <div :class="ns.e('cell-lov-select')">
    <z-select
      :model-value="lovModel"
      :placeholder="column.title"
      :size="size"
      :clearable="!column.required"
      :loading="loading"
      teleported
      @update:model-value="onChange"
      @click.stop
    >
      <z-option
        v-for="item in options"
        :key="item.value"
        :label="item.label"
        :value="item.value"
        :disabled="item.disabled"
      />
    </z-select>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useGlobalConfig } from '@zoryn/components/config-provider'
import { useNamespace } from '@zoryn/hooks'
import ZSelect, { ZOption } from '@zoryn/components/select'
import {
  buildLovCacheKey,
  getLovFromCache,
  useLov,
  useLovCacheVersion,
} from '@zoryn/hooks'
import { resolveLovDisplayField } from '../utils/input-table-picker'
import type { GridColumnInfo } from '../types'
import type { ComponentSize } from '@zoryn/constants'

const DEFAULT_CACHE_TTL = 5 * 60 * 1000

const ns = useNamespace('grid')

const props = defineProps<{
  row: Record<string, unknown>
  column: GridColumnInfo
  size: ComponentSize
}>()

const emit = defineEmits<{
  change: [value: unknown]
}>()

const lovConfig = useGlobalConfig('lov')
const lovCacheVersion = useLovCacheVersion()

/** 与 getLovLabel 一致：首帧同步读缓存，避免 select 在 options 为空时显示裸 value */
const syncOptions = computed(() => {
  void lovCacheVersion.value
  const type = props.column.lovType
  if (!type) {
    return []
  }
  const ttl =
    lovConfig.value?.cacheTtl === undefined
      ? DEFAULT_CACHE_TTL
      : lovConfig.value.cacheTtl
  if (ttl <= 0) {
    return []
  }
  return getLovFromCache(buildLovCacheKey(type), ttl) ?? []
})

const { options, loading, findOption } = useLov(() => props.column.lovType, {
  staticOptions: syncOptions,
})

const lovModel = computed(() => {
  const val = props.row[props.column.field!]
  if (val == null || val === '') {
    return ''
  }
  return String(val)
})

function onChange(value: unknown) {
  const field = props.column.field
  if (!field) {
    return
  }
  const normalized = value == null || value === '' ? null : value
  props.row[field] = normalized

  const displayField = resolveLovDisplayField(props.column)
  if (displayField) {
    const option = findOption(normalized)
    props.row[displayField] = option?.label ?? ''
  }

  emit('change', normalized)
}
</script>
